import { memo } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
} from "@mui/material";

import { ButtonV1 } from "components";

import { DETAILS_VIEW_OPTIONS } from "../constants";
import ColumnsVisibilityMenu from "./ColumnsVisibilityMenu";

const useStatusFilterTones = () => {
  const theme = useTheme();

  return {
    Success: theme.palette.success.dark,
    Warning: theme.palette.warning.darkest,
    Skipped: theme.palette.text.primary,
    Failed: theme.palette.error.dark,
    Abandoned: theme.palette.info.main,
  };
};

const ViewToolbar = ({
  view,
  onViewChange,
  availableStatuses,
  statusFilters,
  onToggleStatusFilter,
  columns,
  hiddenColumns,
  onToggleColumn,
  onClear,
}) => {
  const statusFilterTones = useStatusFilterTones();

  return (
    <Box
      display="flex"
      alignItems="center"
      flexWrap="wrap"
      gap={2}
      sx={{
        border: "1px solid",
        borderColor: "grey.300",
        borderRadius: 2,
        padding: 2,
        mb: 3,
      }}
    >
      <ToggleButtonGroup
        exclusive
        size="small"
        value={view}
        onChange={(_event, newView) => newView && onViewChange(newView)}
      >
        {DETAILS_VIEW_OPTIONS.map(({ value, label }) => (
          <ToggleButton key={value} value={value} sx={{ px: 2 }}>
            {label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <Box display="flex" gap={1} flexWrap="wrap">
        {availableStatuses.map((status) => {
          const isActive = statusFilters.has(status);
          const tone = statusFilterTones[status];

          return (
            <Chip
              key={status}
              label={status.toUpperCase()}
              onClick={() => onToggleStatusFilter(status)}
              variant={isActive ? "filled" : "outlined"}
              sx={{
                fontWeight: 600,
                borderColor: tone,
                color: isActive ? "common.white" : tone,
                backgroundColor: isActive ? tone : "transparent",
                "&:hover": {
                  backgroundColor: isActive ? tone : "transparent",
                },
              }}
            />
          );
        })}
      </Box>

      <ColumnsVisibilityMenu
        columns={columns}
        hiddenColumns={hiddenColumns}
        onToggleColumn={onToggleColumn}
      />

      <ButtonV1
        title="Clear"
        variant="outlined"
        size="small"
        color="inherit"
        style={{ borderColor: "grey.400" }}
        onClick={onClear}
      />
    </Box>
  );
};

ViewToolbar.propTypes = {
  view: PropTypes.string.isRequired,
  onViewChange: PropTypes.func.isRequired,
  availableStatuses: PropTypes.arrayOf(PropTypes.string).isRequired,
  statusFilters: PropTypes.instanceOf(Set).isRequired,
  onToggleStatusFilter: PropTypes.func.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({ key: PropTypes.string, label: PropTypes.string }),
  ).isRequired,
  hiddenColumns: PropTypes.instanceOf(Set).isRequired,
  onToggleColumn: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};

export default memo(ViewToolbar);
